import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getRuntimeConfig } from "nr1";
import { decodeEntityGuid, entityUrl } from "./entity-api";
import {
  defaultVariableValues,
  fetchEntityDetail,
  variableStrategies,
  isDashboard,
  resolveServiceDashboard,
} from "./dashboard-api";
import DashboardView from "./DashboardView";
import HostPanels from "./HostPanels";
import StoreServices from "./StoreServices";
import StoreHosts from "./StoreHosts";

/** The event the compat layer fires in place of the platform's stacked view. */
export const OPEN_ENTITY_EVENT = "healthdashboard:open-entity";

export function openEntityPanel(guid, options) {
  window.dispatchEvent(
    new CustomEvent(OPEN_ENTITY_EVENT, { detail: { guid, options } })
  );
}

/**
 * Standalone replacement for New Relic's stacked entity view.
 *
 * That view cannot be embedded — one.newrelic.com sends X-Frame-Options: DENY —
 * so this rebuilds it from NerdGraph: the entity's own dashboard widgets, its
 * recent violations, and its related entities.
 *
 * Mounted once by the shell, so nothing under src/nerdlets changes; those
 * components still just call navigation.openStackedEntity(guid).
 */
export default function EntityPanel() {
  // A stack rather than a single request: drilling into a related entity should
  // be reversible, the way New Relic's stacked views are.
  const [stack, setStack] = useState([]);

  // Resolved names are kept beside the stack, not inside it.
  //
  // They used to be folded into the entry (`{ ...entry, label: name }`), which
  // replaced the object EntityView receives as `request` — the only dependency
  // of its fetch effect. Naming the entity therefore restarted the whole load:
  // both entity queries again, and every widget query aborted and re-issued.
  const [names, setNames] = useState({});

  const current = stack[stack.length - 1] || null;

  useEffect(() => {
    const onOpen = (event) => {
      const detail = event.detail;
      // Opening from the dashboard starts a fresh trail.
      setStack(detail?.guid ? [detail] : []);
    };
    window.addEventListener(OPEN_ENTITY_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ENTITY_EVENT, onOpen);
  }, []);

  const close = useCallback(() => setStack([]), []);
  const push = useCallback((request) => {
    setStack((prev) => [...prev, request]);
  }, []);
  const back = useCallback(() => setStack((prev) => prev.slice(0, -1)), []);
  const goTo = useCallback((index) => {
    setStack((prev) => prev.slice(0, index + 1));
  }, []);

  // Once an entity resolves, remember its real name for the trail.
  const label = useCallback((guid, name) => {
    setNames((prev) => (prev[guid] === name ? prev : { ...prev, [guid]: name }));
  }, []);

  // Rebuilt on every name change, which is free: the breadcrumbs read it, and
  // nothing fetches off it.
  const trail = useMemo(
    () =>
      stack.map((entry) => ({
        guid: entry.guid,
        label: names[entry.guid] || entry.label || provisionalLabel(entry.guid),
      })),
    [stack, names]
  );

  useEffect(() => {
    if (!current) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      // Step back through the trail rather than losing it all at once.
      stack.length > 1 ? back() : close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [current, stack.length, back, close]);

  if (!current?.guid) return null;
  return (
    <EntityView
      key={current.guid}
      request={current}
      trail={trail}
      onClose={close}
      onBack={stack.length > 1 ? back : null}
      onGoTo={goTo}
      onOpenEntity={push}
      onLabel={label}
    />
  );
}

/** A provisional label, good enough for the trail before the entity loads. */
function provisionalLabel(guid) {
  const decoded = decodeEntityGuid(guid);
  return decoded?.identifier || guid.slice(0, 10);
}

/**
 * Works out which guid to render.
 *
 * The health API supplies a synthesised service entity (`EXT|SERVICE|store`).
 * New Relic shows a dashboard for that entity type, so the same dashboard is
 * resolved here — from the entity's own account — and the store is bound to its
 * `entity` variable, which is how the platform scopes the view.
 *
 * Falls back to rendering the entity itself when no dashboard matches.
 */
async function resolveTarget(request, signal) {
  const { guid, options } = request;
  const decoded = decodeEntityGuid(guid);
  const { entityDashboards } = getRuntimeConfig();
  const supplied = options?.selectedVariables || {};

  const base = { guid, variables: { ...supplied }, sourceGuid: guid, decoded };
  if (!decoded?.identifier || decoded.domain !== "EXT") return base;

  // The caller's service beats the guid's type. The health API stamps every
  // alert guid DIGITALORDERING whatever the service is, so the type names the
  // right dashboard only by accident: in PT the DigitalOrdering dashboard is
  // empty, while the Sesame one these alerts belong to holds nine widgets.
  const dashboardGuid = await resolveServiceDashboard({
    accountId: decoded.accountId,
    type: options?.serviceName || decoded.type,
    overrides: entityDashboards,
    signal,
  });

  if (!dashboardGuid) return base;
  return {
    guid: dashboardGuid,
    variables: { entity: decoded.identifier, ...supplied },
    sourceGuid: guid,
    decoded,
  };
}

function EntityView({ request, trail, onClose, onBack, onGoTo, onOpenEntity, onLabel }) {
  const { region, accountId: configAccountId } = getRuntimeConfig();

  const [target, setTarget] = useState(null);
  const [state, setState] = useState({ loading: true, entity: null, source: null, error: null });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setTarget(null);
    setState({ loading: true, entity: null, source: null, error: null });

    resolveTarget(request, controller.signal)
      .then(async (resolved) => {
        if (cancelled) return;
        setTarget(resolved);

        // The widgets come from the dashboard, but the activity stream and
        // related entities belong to the *store service* entity that was
        // clicked — which is what the platform shows alongside them. When no
        // dashboard was resolved these are the same entity, so only fetch once.
        const needsSource = resolved.sourceGuid !== resolved.guid;
        const [entity, source] = await Promise.all([
          fetchEntityDetail(resolved.guid, { signal: controller.signal }),
          needsSource
            ? fetchEntityDetail(resolved.sourceGuid, { signal: controller.signal })
            : null,
        ]);

        if (!cancelled) {
          setState({ loading: false, entity, source: source || entity, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled && error?.name !== "AbortError") {
          setState({ loading: false, entity: null, source: null, error });
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [request]);

  const { loading, entity, source, error } = state;

  // Feed the real name back so the trail reads like names, not ids.
  useEffect(() => {
    const name = source?.name || entity?.name;
    if (name) onLabel?.(request.guid, name);
  }, [source, entity, request.guid, onLabel]);
  const decoded = target?.decoded ?? decodeEntityGuid(request.guid);

  // Dashboard defaults first, then what the caller bound — the caller wins.
  const variables = useMemo(
    () => ({ ...defaultVariableValues(entity), ...(target?.variables || {}) }),
    [entity, target]
  );

  const strategies = useMemo(() => variableStrategies(entity), [entity]);

  // Only infrastructure hosts have SystemSample/Log keyed by hostname.
  //
  // Gated on the tag rather than the entity type: the GUID encodes this host as
  // domain INFRA, type "NA" — "INFRASTRUCTURE_HOST_ENTITY" is the API's
  // entityType, not something decodeEntityGuid can see. A hostname tag under
  // the INFRA domain is both necessary and sufficient, since it is exactly the
  // key SystemSample and Log are queried by.
  const hostname = useMemo(() => {
    const type = String(entity?.entityType || decoded?.type || "");
    const isHost = decoded?.domain === "INFRA" || type.includes("HOST");
    if (!isHost) return null;
    const tags = source?.tags || entity?.tags || [];
    const tag = tags.find((t) => t.key === "hostname");
    return tag?.values?.[0] || null;
  }, [decoded, source, entity]);

  const dashboard = isDashboard(entity);
  const page = entity?.pages?.[0];

  return (
    <div
      className="entity-overlay"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className={`entity-panel ${dashboard ? "entity-panel--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Entity details"
      >
        <header className="entity-panel__head">
          <div className="entity-panel__heading">
            {trail && trail.length > 1 ? (
              <nav className="entity-trail" aria-label="Entity history">
                <button type="button" className="entity-trail__back" onClick={onBack}>
                  ← Back
                </button>
                <ol>
                  {trail.map((entry, i) => (
                    <li key={`${entry.guid}-${i}`}>
                      {i === trail.length - 1 ? (
                        <span aria-current="page">{entry.label}</span>
                      ) : (
                        <button type="button" onClick={() => onGoTo(i)}>
                          {entry.label}
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              </nav>
            ) : null}
            <p className="entity-panel__eyebrow">
              {decoded ? `${decoded.domain} · ${decoded.type || "entity"}` : "Entity"}
              {variables.entity ? ` · store ${variables.entity}` : ""}
            </p>
            <h2 className="entity-panel__title">
              {entity?.name || decoded?.identifier || "Entity"}
            </h2>
            {page?.name ? <p className="entity-panel__sub">{page.name}</p> : null}
          </div>
          <div className="entity-panel__actions">
            <a
              className="entity-panel__link"
              href={entityUrl(target?.sourceGuid || request.guid, region)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in New Relic ↗
            </a>
            <button type="button" className="entity-panel__close" onClick={onClose} aria-label="Close">
              &#x2715;
            </button>
          </div>
        </header>

        {loading ? <p className="entity-panel__muted">Loading…</p> : null}

        {error ? (
          <div className="entity-panel__error" role="alert">
            <strong>Could not load live detail.</strong>
            <p>{error.message}</p>
            <p className="entity-panel__hint">
              The identifiers below are decoded from the entity ID and remain accurate.
            </p>
            <dl className="entity-facts">
              <Fact label="Account" value={decoded?.accountId} />
              <Fact label="Identifier" value={decoded?.identifier} />
              <Fact label="Domain" value={decoded?.domain} />
              <Fact label="Type" value={decoded?.type} />
            </dl>
          </div>
        ) : null}

        {!loading && !error && !entity ? (
          <p className="entity-panel__muted">
            New Relic has no entity with this ID. It may have been removed, or
            never reported.
          </p>
        ) : null}

        <div className="entity-panel__body">
          <div className="entity-panel__main">
            {dashboard && page ? (
              <DashboardView
                page={page}
                source={source}
                variables={variables}
                strategies={strategies}
                fallbackAccountId={entity?.accountId ?? configAccountId}
              />
            ) : entity ? (
              <EntitySummary entity={entity} decoded={decoded} />
            ) : null}

            {/* Hosts carry their own resource series and logs, which no
                dashboard covers. hostname comes from the entity's own tags —
                it is what SystemSample and Log are both keyed on. */}
            {hostname ? (
              <HostPanels
                hostname={hostname}
                accountId={decoded?.accountId ?? entity?.accountId ?? configAccountId}
              />
            ) : null}
          </div>

          {/* The rail also carries the store's own service lights, so it is
              worth rendering for a store even before the entity resolves. */}
          {source || variables.entity ? (
            <aside className="entity-rail">
              {variables.entity ? <StoreServices storeId={variables.entity} /> : null}
              {variables.entity ? (
                <StoreHosts storeId={variables.entity} onOpenEntity={onOpenEntity} />
              ) : null}
              {source ? <ActivityStream entity={source} /> : null}
              {source ? (
                <RelatedEntities entity={source} onOpenEntity={onOpenEntity} region={region} />
              ) : null}
            </aside>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function EntitySummary({ entity, decoded }) {
  return (
    <>
      <dl className="entity-facts">
        <Fact label="Account" value={entity.accountId ?? decoded?.accountId} />
        <Fact label="Identifier" value={decoded?.identifier} />
        <Fact label="Type" value={entity.entityType} />
        <Fact label="Status" value={entity.reporting ? "Reporting" : "Not reporting"} />
      </dl>

      {entity.tags?.length ? (
        <section className="entity-section">
          <h3>Tags</h3>
          <ul className="entity-tags">
            {entity.tags.slice(0, 16).map((tag) => (
              <li key={tag.key}>
                <span className="entity-tags__key">{tag.key}</span>
                <span className="entity-tags__val">{tag.values.join(", ")}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function ActivityStream({ entity }) {
  const violations = entity.recentAlertViolations || [];
  return (
    <section className="entity-section">
      <h3>Activity stream</h3>
      {entity.alertSeverity ? (
        <p className={`entity-severity entity-severity--${String(entity.alertSeverity).toLowerCase()}`}>
          {entity.alertSeverity.replace(/_/g, " ")}
        </p>
      ) : null}
      {violations.length ? (
        <ul className="entity-violations">
          {violations.map((v) => (
            <li key={v.violationId}>
              <span className={`entity-dot entity-dot--${String(v.level || "").toLowerCase()}`} />
              <span className="entity-violations__label" title={v.label}>
                {v.label}
              </span>
              <span className="entity-violations__time">
                {v.openedAt ? new Date(v.openedAt).toLocaleString() : ""}
                {v.closedAt ? " · closed" : " · open"}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="entity-panel__muted">No recent violations.</p>
      )}
    </section>
  );
}

function RelatedEntities({ entity, onOpenEntity, region }) {
  const results = entity.relatedEntities?.results || [];

  // Collapse to unique targets other than the entity itself; New Relic draws
  // this as a graph, which has no small honest equivalent, so it is a list.
  const targets = useMemo(() => {
    const seen = new Map();
    for (const r of results) {
      const t = r?.target?.entity;
      if (!t?.guid || t.guid === entity.guid || seen.has(t.guid)) continue;
      seen.set(t.guid, { ...t, relationship: r.type });
    }
    return [...seen.values()];
  }, [results, entity.guid]);

  return (
    <section className="entity-section">
      <h3>Related entities</h3>
      {targets.length ? (
        <ul className="entity-related">
          {targets.map((t) => (
            <li key={t.guid}>
              <button
                type="button"
                className="entity-related__item"
                onClick={() => onOpenEntity({ guid: t.guid })}
                title={`${t.entityType}${t.relationship ? ` · ${t.relationship}` : ""}`}
              >
                <span
                  className={`entity-dot entity-dot--${String(t.alertSeverity || "").toLowerCase()}`}
                />
                <span className="entity-related__name">{t.name}</span>
                <span className="entity-related__type">{t.domain}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="entity-panel__muted">No related entities.</p>
      )}
      <a
        className="entity-panel__link entity-panel__link--quiet"
        href={entityUrl(entity.guid, region)}
        target="_blank"
        rel="noopener noreferrer"
      >
        See full map ↗
      </a>
    </section>
  );
}

function Fact({ label, value }) {
  return (
    <div className="entity-facts__item">
      <dt>{label}</dt>
      <dd>{value ?? "—"}</dd>
    </div>
  );
}
